import { useEffect, useCallback, useState } from "react";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { supabase } from "./supabaseClient";
import { normalizeClientType } from "./clientTypes";
import { mergeMessages, type MessageRow } from "./messageMerge";
import { isMissingColumnError } from "./supabaseErrors";

export interface Case {
  id: string;
  title: string;
  company_name: string | null;
  client_first_name: string | null;
  client_last_name: string | null;
  client_email: string | null;
  client_phone: string | null;
  center: string | null;
  secondary_center: string | null;
  activity_type: string | null;
  pax: number | null;
  event_date: string | null;
  event_time_provided: boolean | null;
  budget: number | null;
  source: string | null;
  status: string;
  created_by: string | null;
  created_at: string;
  // Champs Fillout
  occasion: string | null;
  activities: string[] | null;
  extras: string[] | null;
  alt_date: string | null;
  participants: number | null;
  budget_per_person: number | null;
  address: string | null;
  city: string | null;
  postal_code: string | null;
  form_source: string | null;
  form_submission_id: string | null;
  form_submitted_at: string | null;
  // Champ Laser World
  client_type: string | null;
  // Timestamps de suivi commercial
  last_quote_sent_at: string | null;
  last_client_response_at: string | null;
  // Champ import Airtable
  old_airtable_quotes: string | null;
  // Marque (Smile World / Laser World / Vertical World)
  brand: 'smile' | 'laser' | 'vertical' | null;
  // Notes
  client_notes: string | null;
  internal_notes: string | null;
  payment_methods?: string[] | null;
  discovery_source?: string | null;
  // Date de relance téléphonique
  phone_reminder_date: string | null;
  phone_reminder_time?: string | null;
  // Dossier prioritaire (manager, migration 20261104130000)
  is_priority: boolean;
  priority_set_at?: string | null;
  priority_set_by?: string | null;
}

/** Statuts considérés comme archivés (chargés à la demande) */
export const ARCHIVED_STATUSES = ["Supprimé", "Rejeté"];

/**
 * Colonnes nécessaires pour l'affichage Kanban (CaseCard, filtrage, todoCount).
 * Les champs lourds (old_airtable_quotes, client_notes, internal_notes, etc.)
 * sont chargés à la demande quand on ouvre le CaseDrawer.
 */
const KANBAN_SELECT = [
  "id", "title", "company_name",
  "client_first_name", "client_last_name", "client_email", "client_phone",
  "center", "activity_type", "pax", "event_date", "event_time_provided", "budget", "source",
  "status", "created_at", "brand", "client_type",
  "last_quote_sent_at", "last_client_response_at",
  "phone_reminder_date", "participants", "budget_per_person", "secondary_center",
  "is_priority",
].join(",");

const PAGE_SIZE = 1000;

/**
 * Colonnes ajoutées par une migration récente : si le front est déployé avant la migration,
 * la requête Kanban est relancée sans elles (valeur par défaut appliquée par normalizeCaseRecord).
 */
const OPTIONAL_COLUMNS = ["is_priority"] as const;
const missingColumns = new Set<string>();

function withoutMissingColumns(select: string): string {
  if (missingColumns.size === 0) return select;
  return select.split(",").filter((column) => !missingColumns.has(column.trim())).join(",");
}

function normalizeCaseRecord(caseRecord: Case): Case {
  return {
    ...caseRecord,
    client_type: normalizeClientType(caseRecord.client_type),
    // Migration 20261104130000 pas encore appliquée : aucun dossier prioritaire.
    is_priority: !!caseRecord.is_priority,
  };
}

/**
 * Charge un batch de cases depuis Supabase.
 */
async function fetchCasePage(
  page: number,
  select: string,
  excludeStatuses?: string[],
  includeStatuses?: string[],
): Promise<Case[]> {
  const from = page * PAGE_SIZE;
  const to = from + PAGE_SIZE - 1;

  const run = (columns: string) => {
    let query = supabase
      .from("cases")
      .select(columns)
      .order("created_at", { ascending: false })
      .range(from, to);

    if (excludeStatuses && excludeStatuses.length > 0) {
      query = query.not(
        "status",
        "in",
        `(${excludeStatuses.map((s) => `"${s}"`).join(",")})`
      );
    }
    if (includeStatuses && includeStatuses.length > 0) {
      query = query.in("status", includeStatuses);
    }
    return query;
  };

  let columns = withoutMissingColumns(select);
  let { data, error } = await run(columns);
  // Colonne optionnelle absente (front déployé avant la migration) : on relance sans elle.
  for (const optional of OPTIONAL_COLUMNS) {
    if (!error) break;
    if (!columns.split(",").includes(optional) || !isMissingColumnError(error, optional)) continue;
    if (!missingColumns.has(optional)) {
      console.warn(`[useRealtimeCases] Colonne cases.${optional} absente (migration en attente) : chargement sans elle.`);
      missingColumns.add(optional);
    }
    columns = withoutMissingColumns(select);
    ({ data, error } = await run(columns));
  }
  if (error) throw error;
  return ((data as unknown as Case[]) ?? []).map(normalizeCaseRecord);
}

/**
 * Charge tous les cases via des batches parallèles.
 * Lance les pages 0 et 1 en parallèle, puis continue si nécessaire.
 */
async function fetchAllCases(
  select: string,
  excludeStatuses?: string[],
  includeStatuses?: string[],
): Promise<Case[]> {
  // Lancer pages 0 et 1 en parallèle
  const [page0, page1] = await Promise.all([
    fetchCasePage(0, select, excludeStatuses, includeStatuses),
    fetchCasePage(1, select, excludeStatuses, includeStatuses),
  ]);

  let allCases = [...page0, ...page1];

  // Si la page 1 est pleine, continuer séquentiellement (rare)
  if (page1.length === PAGE_SIZE) {
    let page = 2;
    let hasMore = true;
    while (hasMore) {
      const data = await fetchCasePage(page, select, excludeStatuses, includeStatuses);
      allCases = [...allCases, ...data];
      hasMore = data.length === PAGE_SIZE;
      page++;
    }
  }

  return allCases;
}

/**
 * Hook pour charger et écouter les dossiers en temps réel.
 * Les statuts "Supprimé" et "Rejeté" ne sont **pas** chargés au démarrage.
 * Appeler `loadArchivedCases()` pour les charger à la demande.
 */
export function useRealtimeCases() {
  const [cases, setCases] = useState<Case[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [archivedLoaded, setArchivedLoaded] = useState(false);
  const [archivedLoading, setArchivedLoading] = useState(false);

  useEffect(() => {
    // Chargement initial : uniquement les statuts actifs, colonnes ciblées
    const fetchCases = async () => {
      try {
        console.log("[useRealtimeCases] Chargement (select ciblé, 2 batches //)…");
        const t0 = performance.now();

        const activeCases = await fetchAllCases(KANBAN_SELECT, ARCHIVED_STATUSES);

        console.log(`[useRealtimeCases] ✅ ${activeCases.length} cases en ${Math.round(performance.now() - t0)}ms`);

        // Répartition par statut (debug)
        const statusCounts = activeCases.reduce((acc, c) => {
          acc[c.status] = (acc[c.status] || 0) + 1;
          return acc;
        }, {} as Record<string, number>);
        console.log("[useRealtimeCases] Répartition par statut:", statusCounts);

        setCases(activeCases);
        setError(null);
      } catch (err) {
        console.error("[useRealtimeCases] ❌ Erreur:", err);
        setError(err instanceof Error ? err.message : "Failed to load cases");
      } finally {
        setLoading(false);
      }
    };

    fetchCases();

    // Abonnement temps réel (écoute TOUT, y compris archived — nécessaire si un case change de statut)
    console.log("[Realtime Cases] Setup channel pour tous les cases");
    const channel = supabase
      .channel("cases-changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "cases",
        },
        (payload) => {
          console.log("🔔 [Realtime Cases] Changement:", payload.eventType);

          if (payload.eventType === "INSERT") {
            const newCase = normalizeCaseRecord(payload.new as Case);
            // Ignorer les statuts archivés sauf si l'onglet les a déjà chargés
            if (
              ARCHIVED_STATUSES.includes(newCase.status) &&
              !archivedLoaded
            ) {
              return;
            }
            setCases((prev) => {
              if (prev.some((c) => c.id === newCase.id)) return prev;
              return [newCase, ...prev];
            });
          } else if (payload.eventType === "UPDATE") {
            const updated = normalizeCaseRecord(payload.new as Case);
            setCases((prev) => {
              const exists = prev.some((c) => c.id === updated.id);
              if (exists) {
                // Le case existe déjà → mise à jour
                return prev.map((c) => (c.id === updated.id ? updated : c));
              }
              // Le case n'existe pas encore (ex: était archivé, maintenant actif)
              if (!ARCHIVED_STATUSES.includes(updated.status)) {
                return [updated, ...prev];
              }
              return prev;
            });
          } else if (payload.eventType === "DELETE") {
            setCases((prev) => prev.filter((c) => c.id !== payload.old.id));
          }
        }
      )
      .subscribe((status) => {
        console.log(`[Realtime Cases] Status: ${status}`);
      });

    return () => {
      console.log("[Realtime Cases] Cleanup channel");
      supabase.removeChannel(channel);
    };
  }, []);

  /** Charge les cases Supprimé/Rejeté à la demande (select ciblé + batches //) */
  const loadArchivedCases = async () => {
    if (archivedLoaded || archivedLoading) return;
    setArchivedLoading(true);
    try {
      console.log("[useRealtimeCases] Chargement des cases archivés (Supprimé/Rejeté)…");

      const archivedCases = await fetchAllCases(
        KANBAN_SELECT,
        undefined,
        ARCHIVED_STATUSES,
      );

      console.log(`[useRealtimeCases] ✅ ${archivedCases.length} cases archivés chargés`);

      setCases((prev) => {
        const existingIds = new Set(prev.map((c) => c.id));
        const newOnes = archivedCases.filter((c) => !existingIds.has(c.id));
        return [...prev, ...newOnes];
      });

      setArchivedLoaded(true);
    } catch (err) {
      console.error("[useRealtimeCases] ❌ Erreur chargement archivés:", err);
    } finally {
      setArchivedLoading(false);
    }
  };

  return { cases, loading, error, archivedLoaded, archivedLoading, loadArchivedCases };
}

/**
 * Hook pour charger les messages d'un dossier en temps réel
 */
export function useRealtimeMessages(caseId: string) {
  const [messages, setMessages] = useState<MessageRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!caseId) return;

    const channels: RealtimeChannel[] = [];
    let isCancelled = false;

    const fetchMessages = async () => {
      try {
        // Récupérer tous les threads du dossier
        const { data: threads, error: threadsError } = await supabase
          .from("threads")
          .select("id")
          .eq("case_id", caseId);

        if (threadsError || !threads || threads.length === 0) {
          if (!isCancelled) {
            setMessages([]);
            setLoading(false);
          }
          return;
        }

        const threadIds = threads.map((thread) => thread.id);

        // Charger les messages pour tous les threads
        const { data, error: messagesError } = await supabase
          .from("messages")
          .select("*")
          .in("thread_id", threadIds)
          .order("created_at", { ascending: true });

        if (messagesError) throw messagesError;

        if (!isCancelled) {
          setMessages((prev) => mergeMessages(prev, (data as MessageRow[]) || []));
          setLoading(false);
        }

        threadIds.forEach((threadId) => {
          const channel = supabase
            .channel(`messages-${threadId}`)
            .on(
              "postgres_changes",
              {
                event: "INSERT",
                schema: "public",
                table: "messages",
                filter: `thread_id=eq.${threadId}`,
              },
              (payload) => {
                setMessages((prev) =>
                  mergeMessages(prev, [payload.new as MessageRow])
                );
              }
            )
            .subscribe();

          channels.push(channel);
        });
      } catch (err) {
        console.error("Error loading messages:", err);
        setLoading(false);
      }
    };

    fetchMessages();

    return () => {
      isCancelled = true;
      channels.forEach((channel) => supabase.removeChannel(channel));
    };
  }, [caseId]);

  return { messages, loading };
}

/**
 * Hook pour charger les devis d'un dossier en temps réel
 */
export function useRealtimeQuotes(caseId: string) {
  const [quotes, setQuotes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!caseId) return;
      try {
        const { data } = await supabase
          .from("quotes")
          .select("*")
          .eq("case_id", caseId)
          .order("version", { ascending: false });

        setQuotes(data || []);
        setLoading(false);
      } catch (err) {
        console.error("Error loading quotes:", err);
        setLoading(false);
      }
  }, [caseId]);

  useEffect(() => {
    if (!caseId) return;
    refresh();

    // Abonnement temps réel avec filtre côté serveur
    const channel = supabase
      .channel(`quotes-${caseId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "quotes",
          filter: `case_id=eq.${caseId}`,
        },
        (payload) => {
          const quote = payload.new || payload.old;
          if (!quote) return;
            if (payload.eventType === "INSERT") {
              setQuotes((prev) => [payload.new, ...prev]);
            } else if (payload.eventType === "UPDATE") {
              setQuotes((prev) =>
                prev.map((q) => (q.id === payload.new.id ? payload.new : q))
              );
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [caseId, refresh]);

  return { quotes: quotes.filter((quote) => !quote.archived_at), archivedQuotes: quotes.filter((quote) => !!quote.archived_at), loading, refresh };
}
